import { useEffect, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "../lib/api";
import { FileSignature, Plus, X, Copy, Eye, FileDown, RotateCcw } from "lucide-react";

const BAN_CHOICES = [0, 1, 3, 7];

const formOf = (d) => ({
  enabled: d.enabled, consequences: d.consequences, auto_ban_days: d.auto_ban_days, promises: d.promises,
});

const fmt = (iso, withYear = true) => new Date(iso).toLocaleString([], {
  day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}), hour: "numeric", minute: "2-digit",
});

/** Parent's card: write the kid's "I won't delete Yoda Watch" agreement, see it as they will,
 *  turn it on, and download each signed copy as a PDF. */
export default function AgreementCard({ kidId, kidName }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [signed, setSigned] = useState([]);
  const [custom, setCustom] = useState("");
  const [newPromise, setNewPromise] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(false);

  const load = async () => {
    const [{ data: d }, { data: s }] = await Promise.all([
      api.get(`/kids/${kidId}/agreement`),
      api.get(`/kids/${kidId}/agreement/signed`).catch(() => ({ data: [] })),
    ]);
    setData(d);
    setForm(formOf(d));
    setSigned(s);
  };
  useEffect(() => { load().catch(() => {}); }, [kidId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !form) return null;
  const link = data.sign_token ? `${window.location.origin}/agreement/${data.sign_token}` : "";
  const dirty = JSON.stringify(form) !== JSON.stringify(formOf(data));
  const pdfUrl = (id) => `${API_BASE}/kids/${kidId}/agreement/signed/${id}/pdf`;

  const save = async (next = form) => {
    setBusy(true);
    setNote("");
    try {
      const { data: d } = await api.put(`/kids/${kidId}/agreement`, next);
      setData(d);
      setForm(formOf(d));
      if (d.enabled && !d.signed) setNote(`Saved. ${kidName}'s devices stay blocked until they sign.`);
      else setNote("Saved.");
    } catch (e) {
      setNote(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleConsequence = (c) => setForm((f) => ({
    ...f, consequences: f.consequences.includes(c) ? f.consequences.filter((x) => x !== c) : [...f.consequences, c],
  }));
  const addCustom = () => {
    const c = custom.trim();
    if (!c) return;
    setForm((f) => ({ ...f, consequences: f.consequences.includes(c) ? f.consequences : [...f.consequences, c] }));
    setCustom("");
  };
  const setPromise = (i, text) => setForm((f) => ({ ...f, promises: f.promises.map((p, j) => (j === i ? text : p)) }));
  const removePromise = (i) => setForm((f) => ({ ...f, promises: f.promises.filter((_, j) => j !== i) }));
  const addPromise = () => {
    const p = newPromise.trim();
    if (!p) return;
    setForm((f) => ({ ...f, promises: [...f.promises, p] }));
    setNewPromise("");
  };
  const standardWording = JSON.stringify(form.promises) === JSON.stringify(data.default_promises);
  const options = [...data.suggested_consequences, ...form.consequences.filter((c) => !data.suggested_consequences.includes(c))];

  // What the kid will read, built from the form so parents see changes before saving.
  const doc = {
    title: data.document.title,
    intro: data.document.intro,
    promises: form.promises.map((p) => p.trim()).filter(Boolean),
    consequences: [
      ...form.consequences,
      ...(form.auto_ban_days ? [`If Yoda Watch spots that it was deleted or switched off, my screen time stops for ${form.auto_ban_days} day${form.auto_ban_days > 1 ? "s" : ""} straight away.`] : []),
    ],
  };

  return (
    <div data-testid="agreement-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E] flex items-center gap-2">
            <FileSignature size={13} /> Agreement
          </div>
          <h3 className="font-heading text-2xl font-medium text-[#2C342A] mt-1">Don't delete Yoda Watch</h3>
          <p className="text-sm text-[#707A6E] mt-1 max-w-2xl">
            {kidName} reads and signs a short agreement not to delete or switch off Yoda Watch. Write it below, then
            turn it on. Until they sign, their devices stay blocked, the same as tasks to do. Each signed copy is
            saved as a PDF.
          </p>
          <button type="button" onClick={() => setPreview(true)} data-testid="agreement-preview"
                  className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#E4E2DC] bg-white px-3 py-2 text-sm font-medium hover:bg-[#F5F4F0]">
            <Eye size={15} /> See it as {kidName} will
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium shrink-0">
          <input type="checkbox" className="h-5 w-5" checked={form.enabled}
                 onChange={(e) => { const next = { ...form, enabled: e.target.checked }; setForm(next); save(next); }}
                 data-testid="agreement-toggle" />
          {form.enabled ? "On" : "Off"}
        </label>
      </div>

      <div className="mt-5 grid gap-6 md:grid-cols-2">
        <div>
          <div className="text-sm font-medium text-[#2C342A]">The promises</div>
          <p className="text-xs text-[#707A6E]">Change the wording, remove any, or add your own.</p>
          <ol className="mt-2 space-y-1.5" data-testid="agreement-promises">
            {form.promises.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-2 w-4 text-right text-sm text-[#707A6E]">{i + 1}.</span>
                <textarea value={p} rows={2} maxLength={200} onChange={(e) => setPromise(i, e.target.value)}
                          className="min-w-0 flex-1 resize-y rounded-lg border border-[#E4E2DC] px-3 py-1.5 text-sm" />
                <button type="button" title="Remove this promise" onClick={() => removePromise(i)}
                        disabled={form.promises.length <= 1} className="mt-2 text-[#A6ABA3] disabled:opacity-30">
                  <X size={14} />
                </button>
              </li>
            ))}
          </ol>
          <div className="mt-2 flex gap-2">
            <input value={newPromise} onChange={(e) => setNewPromise(e.target.value)} maxLength={200}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPromise(); } }}
                   placeholder="Add a promise" className="min-w-0 flex-1 rounded-lg border border-[#E4E2DC] px-3 py-2 text-sm" />
            <button type="button" onClick={addPromise} className="rounded-lg border border-[#E4E2DC] px-3 text-sm"><Plus size={14} /></button>
          </div>
          {!standardWording && (
            <button type="button" onClick={() => setForm((f) => ({ ...f, promises: data.default_promises }))}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-[#707A6E] underline">
              <RotateCcw size={12} /> Back to the standard promises
            </button>
          )}

          <div className="mt-5 text-sm font-medium text-[#2C342A]">If they break it</div>
          <p className="text-xs text-[#707A6E]">Pick any of these, or add your own. They're written into the agreement.</p>
          <div className="mt-2 space-y-1.5">
            {options.map((c) => (
              <label key={c} className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={form.consequences.includes(c)} onChange={() => toggleConsequence(c)} />
                <span>{c}</span>
                {!data.suggested_consequences.includes(c) && (
                  <button type="button" onClick={() => toggleConsequence(c)} className="text-[#A6ABA3]"><X size={14} /></button>
                )}
              </label>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={custom} onChange={(e) => setCustom(e.target.value)} maxLength={200}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                   placeholder="Your own consequence" className="min-w-0 flex-1 rounded-lg border border-[#E4E2DC] px-3 py-2 text-sm" />
            <button type="button" onClick={addCustom} className="rounded-lg border border-[#E4E2DC] px-3 text-sm"><Plus size={14} /></button>
          </div>

          <div className="mt-4 text-sm font-medium text-[#2C342A]">Automatic consequence</div>
          <p className="text-xs text-[#707A6E]">
            When Yoda Watch spots it was deleted or switched off (on an iPhone, iPad or the PC), stop their screen time for:
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {BAN_CHOICES.map((d) => (
              <button key={d} type="button" onClick={() => setForm((f) => ({ ...f, auto_ban_days: d }))}
                      className={`rounded-full border px-3 py-1 text-sm ${form.auto_ban_days === d ? "bg-[#5C7A4F] text-white border-[#5C7A4F]" : "border-[#E4E2DC]"}`}>
                {d === 0 ? "Off" : `${d} day${d > 1 ? "s" : ""}`}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" disabled={!dirty || busy} onClick={() => save()}
                    className="rounded-xl bg-[#5C7A4F] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-40"
                    data-testid="agreement-save">
              {busy ? "Saving…" : data.signed && dirty ? "Save (they'll sign again)" : "Save"}
            </button>
            {dirty && (
              <button type="button" onClick={() => setForm(formOf(data))} className="text-sm text-[#707A6E] underline">
                Undo changes
              </button>
            )}
          </div>
          {note && <p className="mt-2 text-xs text-[#5C7A4F]">{note}</p>}
        </div>

        <div>
          <div className="text-sm font-medium text-[#2C342A]">Signing</div>
          {data.banned_until && (
            <div className="mt-2 rounded-xl bg-[#C06E52]/10 px-3 py-2 text-sm text-[#A35A42]">
              Agreement broken{data.breach?.what ? ` (Yoda Watch was ${data.breach.what})` : ""}. Screen time stopped until{" "}
              {new Date(data.banned_until).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}.
              <button type="button" className="ml-2 underline" onClick={async () => { await api.post(`/kids/${kidId}/agreement/lift`); load(); }}>
                Lift it now
              </button>
            </div>
          )}
          {data.signed ? (
            <div className="mt-2 rounded-xl border border-[#F0EFEB] bg-white p-3">
              <p className="text-sm">Signed by <b>{data.signed_name}</b> on {fmt(data.signed_at)}</p>
              {data.signature && <img src={data.signature} alt={`${data.signed_name}'s signature`} className="mt-2 max-h-24" />}
              {signed[0] && (
                <a href={pdfUrl(signed[0].id)} target="_blank" rel="noopener noreferrer" data-testid="agreement-pdf"
                   className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#5C7A4F] px-3 py-2 text-sm font-medium text-white">
                  <FileDown size={15} /> Download signed PDF
                </a>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm text-[#C06E52]">
              {!form.enabled ? "Turned off, so nothing to sign." :
                data.ever_signed ? "Changed since they signed. Waiting for them to sign the new version." : "Not signed yet."}
            </p>
          )}
          <p className="mt-3 text-xs text-[#707A6E]">
            {kidName} signs with a finger in the Yoda Watch app on their iPhone or iPad, or on any phone or computer with this link:
          </p>
          {link && (
            <div className="mt-1 flex items-center gap-2">
              <a href={link} target="_blank" rel="noopener noreferrer" className="truncate text-sm underline text-[#5C7A4F]">Open signing page</a>
              <button type="button" title="Copy link" className="text-[#707A6E]"
                      onClick={() => { navigator.clipboard?.writeText(link); setNote("Link copied."); }}>
                <Copy size={14} />
              </button>
            </div>
          )}

          {signed.length > 0 && (
            <div className="mt-5">
              <div className="text-sm font-medium text-[#2C342A]">Signed copies</div>
              <ul className="mt-1 divide-y divide-[#F0EFEB] text-sm">
                {signed.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="text-[#2C342A]">
                      {fmt(s.at)} <span className="text-[#707A6E]">· {s.name} · version {s.version}</span>
                    </span>
                    <a href={pdfUrl(s.id)} target="_blank" rel="noopener noreferrer"
                       className="inline-flex shrink-0 items-center gap-1 text-[#5C7A4F] underline">
                      <FileDown size={13} /> PDF
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-4 text-xs text-[#707A6E]">
            Tip: on their iPhone or iPad, Settings &gt; Screen Time &gt; Content &amp; Privacy Restrictions &gt; App Store
            Purchases &gt; Deleting Apps &gt; Don't Allow stops apps being deleted at all.
          </p>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(false)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-[#F5F4F0] p-6 shadow-xl"
               onClick={(e) => e.stopPropagation()} data-testid="agreement-preview-sheet">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs text-[#707A6E]">
                This is what {kidName} reads{dirty ? ", including the changes you haven't saved yet" : ""}.
              </p>
              <button type="button" onClick={() => setPreview(false)} className="text-[#707A6E]" title="Close"><X size={18} /></button>
            </div>
            <h2 className="font-heading text-3xl font-semibold text-[#2C342A] mt-2">{doc.title}</h2>
            <p className="mt-2 text-[#2C342A]/80">{doc.intro}</p>
            <div className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">I promise</div>
            <ul className="mt-2 space-y-2">
              {doc.promises.map((p, i) => (
                <li key={i} className="flex gap-3 rounded-xl bg-white p-3 text-[#2C342A]">
                  <span className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-[#A6ABA3]" />
                  {p}
                </li>
              ))}
            </ul>
            {doc.consequences.length > 0 && (
              <>
                <div className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">If I break this agreement</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-[#2C342A]">
                  {doc.consequences.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </>
            )}
            <div className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Sign</div>
            <div className="mt-2 rounded-xl bg-white px-3 py-2 text-[#A6ABA3]">Their name</div>
            <div className="mt-2 flex h-24 items-center justify-center rounded-xl border-2 border-dashed border-[#E4E2DC] bg-white text-[#A6ABA3]">
              Sign here with your finger
            </div>
            <p className="mt-4 text-xs text-[#707A6E]">
              They tick every promise, type their name and sign. You get an email with the signed PDF.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
